import React, { useContext } from 'react';
import { BpaContext, IBpaContext, IBpaTariff } from '../../../bpaState/bpaContext';
import { BpaPaymentSystem, CardBrands } from 'types';
import { cardBrands } from 'appConstants';
import { useTranslation } from 'react-i18next';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { Icon, IconName } from 'components/Icon/Icon';
import Text from 'components/Text/Text';
import { BpaPaymentSystemSelect } from './BpaPaymentSystemSelect';
import { CardBrandSelect } from './CardBrandSelect';
import { BankFeeField } from './BankFeeField';
import { KmFeeField } from './KmFeeField';

interface IProps {
	setId: string;
	tariff: IBpaTariff;
	tariffs: IBpaTariff[];
}

export const Tariff: React.FC<IProps> = ({ setId, tariff, tariffs }) => {
	const { t } = useTranslation('form');
	const {
		deleteTariff,
		addCardBrand,
		deleteCardBrand,
		changeBpaPaymentSystem,
		changeCardBrand,
		changeBankFee,
		changeKMFee,
	} = useContext<IBpaContext>(BpaContext);
	const tariffId = tariff.id;
	const hasCardBrands =
		tariff.typeOfBpaPaymentSystem === BpaPaymentSystem.M4Bank ||
		tariff.typeOfBpaPaymentSystem === BpaPaymentSystem.Tieto;

	const handleChangeCardBrand = ({ brandId, value }: { brandId: string; value: CardBrands }) => {
		changeCardBrand({ setId, tariffId, brandId, value });
	};

	const handleChangeBankFee = ({ brandId, value }: { brandId: string; value: Nullable<number> }) => {
		changeBankFee({ setId, tariffId, brandId, value });
	};

	const handleChangeKmFee = ({ brandId, value }: { brandId: string; value: Nullable<number> }) => {
		changeKMFee({ setId, tariffId, brandId, value });
	};

	return (
		<>
			<div className="flex items-start gap-8">
				<BpaPaymentSystemSelect
					tariffId={tariffId}
					tariffs={tariffs}
					onChange={(value) => changeBpaPaymentSystem({ setId, tariffId, value })}
				/>
				{tariffs.length !== 1 ? (
					<NewButton
						className="mt-7.5"
						variant="shadow"
						color="red"
						size="small"
						icon={true}
						iconLeft={<Icon name={IconName.delete} size="lg" />}
						ariaLabel={t('deleteButton')}
						testId="DeleteTariffBtn"
						onClick={() => deleteTariff({ setId, tariffId })}
					/>
				) : (
					<div className="w-10 shrink-0" />
				)}
			</div>
			{hasCardBrands && (
				<>
					{tariff.data.map((el, i) => (
						<div key={el.id} className="flex items-start gap-8">
							<CardBrandSelect brandId={el.id} brands={tariff.data} onChange={handleChangeCardBrand} />
							<BankFeeField brandId={el.id} onChange={handleChangeBankFee} />
							<KmFeeField brandId={el.id} onChange={handleChangeKmFee} />
							{i > 0 ? (
								<NewButton
									className="mt-7.5"
									variant="shadow"
									color="red"
									size="small"
									icon={true}
									iconLeft={<Icon name={IconName.delete} size="lg" />}
									ariaLabel={t('deleteButton')}
									testId="DeleteCardBrandBtn"
									onClick={() => deleteCardBrand({ setId, tariffId, brandId: el.id })}
								/>
							) : (
								<div className="w-10 shrink-0" />
							)}
						</div>
					))}
					<div>
						<NewButton
							size="small"
							iconLeft={<Icon name={IconName.plus} size="lg" />}
							disabled={tariff.data.length >= cardBrands.length}
							testId="AddCardBrandBtn"
							onClick={() => addCardBrand({ setId, tariffId })}>
							<Text translationNameSpace="form" translationKey="addButton" asFragment={true} />
						</NewButton>
					</div>
				</>
			)}
			{tariff.typeOfBpaPaymentSystem === BpaPaymentSystem.SBP && !!tariff.data.length && (
				<div className="flex items-start gap-8">
					<BankFeeField brandId={tariff.data[0].id} onChange={handleChangeBankFee} />
					<KmFeeField brandId={tariff.data[0].id} onChange={handleChangeKmFee} />
					<div className="w-10 shrink-0" />
				</div>
			)}
		</>
	);
};
